// ============================================================
// Net Exposure — Risk note drawer (left side; DOM Trader keeps the right).
//
// Opened by the "Risk note" button on a symbol (or unifier) row. Each open and
// each "New note" is a fresh analysis: the exposure snapshot is taken at that
// moment, the NexDay prediction is fetched again, and the summary is asked
// for again. The note is timestamped and never changes after it is made.
//
// Summary: POST /api/v1/risk-note/summary (the model words the facts built in
// riskNote.ts). When it is unavailable, the summary written in code is shown.
// ============================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PortfolioExposureSymbolRow } from '@/services/api';
import { buildRiskNote, RISK_NOTE_DISCLAIMER, type NoteLegIn, type RiskNote, type S360Lite } from './riskNote';

const API_BASE = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

const C = {
  red: '#d07070', green: '#6aaa78', amber: '#c09060', teal: '#49b3b3',
  panel: '#1a1a1c', panel2: '#2a292c', field: '#232225', border: '#3a3940',
  text: '#E6E6E6', soft: '#d2d6e2', pos: '#66e07a', neg: '#ff6b6b',
};

export interface RiskNoteRequest { key: string; isUnifier: boolean; nonce: number }

export interface RiskNoteSnapshot {
  legs: NoteLegIn[];
  rows: PortfolioExposureSymbolRow[];
  asOf: string | null;
}

interface S360SymbolRow { symbol: string; mappings: { platform: 'MT5' | 'CTRADER'; symbol: string }[] }

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } });
  if (!res.ok) throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status });
  return res.json();
}

type SummaryState =
  | { kind: 'loading' }
  | { kind: 'ai'; headline: string; points: string[] }
  | { kind: 'code' };

const statusColor = (s: string) =>
  s === 'MATCHED' ? C.green : s === 'PARTIAL' || s === 'OVER' ? C.amber : s === 'FLAT' ? C.soft : C.red;

const fmtStamp = (d: Date) => {
  const local = d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const et = d.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' });
  return `${local} (${et} New York)`;
};

export function RiskNoteDrawer({ request, snapshot, onClose }: {
  request: RiskNoteRequest | null;
  /** Reads the page's live data at the moment of the call. */
  snapshot: (key: string, isUnifier: boolean) => RiskNoteSnapshot;
  onClose: () => void;
}) {
  const [note, setNote] = useState<RiskNote | null>(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState<SummaryState>({ kind: 'loading' });
  // Full screen over the page; Restore (or Esc) brings back the drawer.
  const [maxi, setMaxi] = useState(false);
  useEffect(() => {
    if (!maxi) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaxi(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maxi]);
  useEffect(() => { if (!request) setMaxi(false); }, [request]);
  const symbolsRef = useRef<S360SymbolRow[] | null>(null);
  const runRef = useRef(0);
  const snapshotRef = useRef(snapshot);
  useEffect(() => { snapshotRef.current = snapshot; }, [snapshot]);

  const generate = useCallback(async (key: string, isUnifier: boolean) => {
    const run = ++runRef.current;
    setBusy(true);
    setNote(null);
    setSummary({ kind: 'loading' });
    const now = new Date();
    const snap = snapshotRef.current(key, isUnifier);

    // NexDay symbol for the instrument: a mapped member, biggest net first.
    let prediction: S360Lite | null = null;
    let predictionNote: string | undefined;
    try {
      if (!symbolsRef.current) {
        const r = await getJson<{ symbols: S360SymbolRow[] }>('/api/v1/predictions/symbol360/symbols');
        symbolsRef.current = r.symbols ?? [];
      }
      const members = [...snap.rows]
        .sort((a, b) => Math.abs(b.net_exposure_lots) - Math.abs(a.net_exposure_lots))
        .map((r) => r.symbol);
      if (!members.length) members.push(key);
      let nexday: string | null = null;
      for (const m of members) {
        for (const plat of ['MT5', 'CTRADER'] as const) {
          const hit = symbolsRef.current.find((s) => s.mappings?.some((x) => x.platform === plat && x.symbol === m));
          if (hit) { nexday = hit.symbol; break; }
        }
        if (nexday) break;
      }
      if (!nexday) predictionNote = `${key} has no NexDay mapping (Settings, Predictions)`;
      else prediction = await getJson<S360Lite>(`/api/v1/predictions/symbol360?symbol=${encodeURIComponent(nexday)}`);
    } catch (e) {
      const st = (e as { status?: number }).status;
      predictionNote = st === 403 ? 'no access to predictions'
        : st === 503 ? 'the NexDay service is not running'
        : 'NexDay predictions could not be loaded';
    }
    if (run !== runRef.current) return;

    const built = buildRiskNote({
      key, isUnifier, legs: snap.legs, rows: snap.rows, exposureAsOf: snap.asOf,
      prediction, predictionNote, now,
    });
    setNote(built);
    setBusy(false);

    try {
      const res = await fetch(`${API_BASE}/api/v1/risk-note/summary`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ as_of: now.toISOString(), facts: built.facts }),
      });
      const body = res.ok ? await res.json() : null;
      if (run !== runRef.current) return;
      if (body?.ok && body.summary?.headline) {
        setSummary({ kind: 'ai', headline: body.summary.headline, points: body.summary.points ?? [] });
      } else {
        setSummary({ kind: 'code' });
      }
    } catch {
      if (run === runRef.current) setSummary({ kind: 'code' });
    }
  }, []);

  useEffect(() => {
    if (request) void generate(request.key, request.isUnifier);
  }, [request, generate]);

  if (!request) return null;

  const copyText = () => {
    if (!note) return;
    const s = summary.kind === 'ai' ? summary : note.summary;
    const lines = [
      `Risk note: ${note.key} (all platforms)`,
      `Generated ${fmtStamp(note.generatedAt)}`,
      '',
      s.headline, ...s.points.map((p) => `- ${p}`),
      '',
      'Plan:',
      ...note.plan.map((p) => `- ${p.when}: ${p.action}${p.size ? `, ${p.size}` : ''}${p.price ? `, ${p.price}` : ''}${p.venue ? ` (${p.venue})` : ''}${p.netAfter ? `. Net after ${p.netAfter}` : ''}`),
      ...(note.triggers.length ? ['', 'Triggers:', ...note.triggers.map((t) => `- ${t}`)] : []),
      ...(note.donts.length ? ['', 'Do not:', ...note.donts.map((t) => `- ${t}`)] : []),
      '', ...RISK_NOTE_DISCLAIMER,
    ];
    void navigator.clipboard?.writeText(lines.join('\n')).catch(() => {});
  };

  const btn = 'px-2.5 py-1 text-xs rounded border transition-colors';
  const shownSummary = summary.kind === 'ai' ? summary : note?.summary;

  return (
    <aside className="flex flex-col min-h-0" style={{
      background: C.panel, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.teal}`, borderRadius: 4,
      ...(maxi
        ? { position: 'fixed', inset: 12, zIndex: 1000, boxShadow: '0 12px 48px rgba(0,0,0,0.6)' }
        : { width: 540, flexShrink: 0 }),
    }}>
      {/* Header */}
      {/* Title and buttons wrap onto two lines when the drawer is narrow;
          nothing in the title ever wraps or sits under a button. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="min-w-0" style={{ whiteSpace: 'nowrap' }}>
          <div className="flex items-center gap-2" style={{ flexWrap: 'nowrap' }}>
            <span className="font-semibold text-white" style={{ fontSize: 15 }}>Risk note</span>
            <span className="font-mono font-semibold" style={{ fontSize: 14, color: C.teal }}>{request.key}</span>
            <span style={{ fontSize: 11, color: C.soft, border: '1px solid #808080', borderRadius: 3, padding: '0 6px', lineHeight: '16px' }}>All platforms</span>
          </div>
          <div style={{ fontSize: 12, color: C.soft, marginTop: 2 }}>
            {note ? <>Generated <span className="font-mono">{fmtStamp(note.generatedAt)}</span></> : 'Generating…'}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <button className={btn} style={{ color: C.text, borderColor: '#606060' }} disabled={busy}
            onClick={() => void generate(request.key, request.isUnifier)} title="Make a new note from the figures now">New note</button>
          <button className={btn} style={{ color: C.text, borderColor: '#606060' }} disabled={!note}
            onClick={copyText} title="Copy the note as text">Copy</button>
          <button className={btn} style={{ color: C.text, borderColor: '#606060' }} onClick={() => setMaxi((m) => !m)}
            title={maxi ? 'Back to the drawer (Esc)' : 'Full screen'}>{maxi ? 'Restore' : 'Maximise'}</button>
          <button className={btn} style={{ color: C.text, borderColor: '#606060' }} onClick={onClose} title="Close">Close</button>
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto ${maxi ? 'px-6 py-4' : 'px-4 py-3'}`} style={{ fontSize: maxi ? 14 : 13, color: C.text }}>
        {!note ? (
          <div style={{ color: C.soft, padding: '24px 0' }}>Reading the book and the NexDay prediction…</div>
        ) : (
          <>
            <div style={maxi ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 32 } : undefined}>
            <div>
            {/* Summary */}
            <Section title="Summary">
              {summary.kind === 'loading' ? (
                <div style={{ color: C.soft }}>Writing the summary…</div>
              ) : shownSummary && (
                <>
                  <div className="font-semibold text-white" style={{ fontSize: 14, lineHeight: 1.45 }}>{shownSummary.headline}</div>
                  <ul style={{ margin: '6px 0 0 16px', listStyle: 'disc' }}>
                    {shownSummary.points.map((p, i) => <li key={i} style={{ lineHeight: 1.5, marginTop: 2 }}>{p}</li>)}
                  </ul>
                </>
              )}
            </Section>

            {/* Position */}
            <Section title="Position">
              <div className="grid grid-cols-4 gap-2 mb-2">
                <Kpi label="Net" value={`${note.net.side} ${note.net.units}`} />
                <Kpi label="Floating P/L" value={note.net.pnl} tone={note.net.pnl.startsWith('-') ? C.neg : C.pos} />
                <Kpi label="Status" value={note.status} tone={statusColor(note.status)} />
                <Kpi label="Hedge ratio" value={note.ratio} />
              </div>
              <div style={{ color: C.soft, fontSize: 12, marginBottom: 6 }}>
                Price <span className="font-mono" style={{ color: C.text }}>{note.price}</span>
                {' · '}Sensitivity <span className="font-mono" style={{ color: C.text }}>{note.sensitivity}</span>
                {' · '}Clients <span className="font-mono" style={{ color: C.text }}>{note.clients.side} {note.clients.units}</span>
                {' · '}Cover <span className="font-mono" style={{ color: C.text }}>{note.cover.side} {note.cover.units}</span>
              </div>
              <table className="w-full" style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: C.soft, textAlign: 'left' }}>
                    {['Leg', 'Side', 'Size', 'B/E', 'Mkt', 'P/L'].map((h, i) => (
                      <th key={h} style={{ fontWeight: 500, padding: '4px 6px', textAlign: i >= 2 ? 'right' : 'left', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {note.legs.map((l) => (
                    <tr key={l.id} style={{ borderBottom: '1px solid #2e2d34' }}>
                      <td style={{ padding: '4px 6px', color: l.book === 'Clients' ? C.teal : C.amber }}>
                        {l.label}{l.stale ? ' (not live)' : ''}
                        <div style={{ color: C.soft, fontSize: 11 }}>{l.book} · {l.account}{l.symbol !== note.key ? ` · ${l.symbol}` : ''}</div>
                      </td>
                      <td style={{ padding: '4px 6px' }}>{l.side}</td>
                      <td className="font-mono" style={{ padding: '4px 6px', textAlign: 'right' }}>{l.units}</td>
                      <td className="font-mono" style={{ padding: '4px 6px', textAlign: 'right' }}>{l.breakEven}</td>
                      <td className="font-mono" style={{ padding: '4px 6px', textAlign: 'right' }}>{l.price}</td>
                      <td className="font-mono" style={{ padding: '4px 6px', textAlign: 'right', color: l.pnlValue < 0 ? C.neg : l.pnlValue > 0 ? C.pos : C.text }}>{l.pnl}{l.unpriced ? ' *' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            {/* Where the issue is */}
            <Section title="Where the risk comes from">
              <ul style={{ margin: '0 0 0 16px', listStyle: 'disc' }}>
                {note.where.map((w, i) => <li key={i} style={{ lineHeight: 1.5, marginTop: 2 }}>{w}</li>)}
              </ul>
            </Section>

            </div>
            <div>
            {/* NexDay */}
            <Section title="NexDay prediction">
              {!note.forecast ? (
                <div style={{ color: C.soft }}>{note.forecastNote}</div>
              ) : (
                <>
                  <div style={{ lineHeight: 1.55 }}>
                    <span className="font-mono">{note.forecast.nexdaySymbol}</span>, for <span className="font-mono">{note.forecast.targetDate}</span>
                    {note.forecast.stale && <span style={{ color: C.amber }}> (not today)</span>}.
                    {' '}Trend <b>{note.forecast.trend}</b> ({note.forecast.trendDirection}), momentum {note.forecast.momentum},
                    {' '}conviction <b>{note.forecast.conviction}</b>, opportunity <b>{note.forecast.opportunity}</b>.
                  </div>
                  <div style={{ marginTop: 4 }}>
                    Daily range <span className="font-mono">{note.forecast.dailyLow}</span> to <span className="font-mono">{note.forecast.dailyHigh}</span>,
                    {' '}midpoint <span className="font-mono">{note.forecast.midpoint}</span>.
                    {note.forecast.levels && <>
                      {' '}Trading levels ({note.forecast.levels.side === 'LONG' ? 'Long' : 'Short'}): entry <span className="font-mono">{note.forecast.levels.entry[1]}–{note.forecast.levels.entry[0]}</span>,
                      {' '}stop <span className="font-mono">{note.forecast.levels.sl[0]}–{note.forecast.levels.sl[1]}</span>,
                      {' '}target <span className="font-mono">{note.forecast.levels.tp[1]}–{note.forecast.levels.tp[0]}</span>.
                    </>}
                  </div>
                  {note.forecast.shift && (
                    <div style={{ marginTop: 6, padding: '6px 8px', border: `1px solid #8a6d44`, borderRadius: 3, color: C.amber, fontSize: 12 }}>
                      Price basis {note.forecast.shift.value}. {note.forecast.shift.reason}
                    </div>
                  )}
                  {note.forecast.windows.length > 0 && (
                    <table className="w-full mt-2" style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ color: C.soft }}>
                          {['Window', 'Time', 'Low', 'High', 'Trend'].map((h, i) => (
                            <th key={h} style={{ fontWeight: 500, padding: '4px 6px', textAlign: i >= 2 && i <= 3 ? 'right' : 'left', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {note.forecast.windows.map((w) => (
                          <tr key={w.label} style={{ opacity: w.elapsed ? 0.45 : 1, borderBottom: '1px solid #2e2d34' }} title={w.elapsed ? 'Window has ended' : undefined}>
                            <td style={{ padding: '4px 6px' }}>{w.label}{w.elapsed ? ' (ended)' : ''}</td>
                            <td className="font-mono" style={{ padding: '4px 6px' }}>{tm(w.start)}–{tm(w.end)}</td>
                            <td className="font-mono" style={{ padding: '4px 6px', textAlign: 'right' }}>{w.low}</td>
                            <td className="font-mono" style={{ padding: '4px 6px', textAlign: 'right' }}>{w.high}</td>
                            <td style={{ padding: '4px 6px', color: w.trend === 'Up' ? C.green : w.trend === 'Down' ? C.red : C.text }}>{w.trend}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </Section>

            {/* Plan */}
            <Section title="What to do, how, when">
              <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {note.plan.map((s, i) => (
                  <li key={i} style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: 8, padding: '7px 0', borderBottom: '1px solid #2e2d34' }}>
                    <span className="font-mono" style={{ color: C.teal, fontSize: 12, paddingTop: 1 }}>{i + 1}</span>
                    <div>
                      <div style={{ lineHeight: 1.45 }}>
                        <span className={/\d/.test(s.when) ? 'font-mono font-semibold' : 'font-semibold'} style={{ color: C.text, marginRight: 8 }}>{s.when}</span>
                        {s.action}
                      </div>
                      {(s.size || s.price || s.netAfter || s.venue) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1" style={{ marginTop: 3, fontSize: 12, color: C.soft }}>
                          {s.size && <span>Size <span className="font-mono" style={{ color: C.text }}>{s.size}</span></span>}
                          {s.price && <span>Price <span className="font-mono" style={{ color: C.text }}>{s.price}</span></span>}
                          {s.netAfter && <span>Net after <span className="font-mono" style={{ color: C.text }}>{s.netAfter}</span></span>}
                          {s.venue && <span>{s.venue}</span>}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
              {note.triggers.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div className="font-semibold text-white" style={{ fontSize: 12, marginBottom: 2 }}>Triggers, any time today</div>
                  <ul style={{ margin: '0 0 0 16px', listStyle: 'disc' }}>
                    {note.triggers.map((t, i) => <li key={i} style={{ lineHeight: 1.5 }}>{t}</li>)}
                  </ul>
                </div>
              )}
            </Section>

            {note.donts.length > 0 && (
              <Section title="Do not">
                <ul style={{ margin: '0 0 0 16px', listStyle: 'disc' }}>
                  {note.donts.map((t, i) => <li key={i} style={{ lineHeight: 1.5, marginTop: 2 }}>{t}</li>)}
                </ul>
              </Section>
            )}

            </div>
            </div>

            {/* Disclaimer */}
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 11, lineHeight: 1.5, color: C.soft }}>
              <div className="font-semibold" style={{ color: C.text, marginBottom: 2 }}>Disclaimer</div>
              {RISK_NOTE_DISCLAIMER.map((t, i) => <p key={i} style={{ marginTop: i ? 4 : 0 }}>{t}</p>)}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

const tm = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 14 }}>
      <div className="font-semibold" style={{ fontSize: 12, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.soft, marginBottom: 6 }}>{title}</div>
      {children}
    </section>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={{ background: C.field, border: `1px solid ${C.border}`, borderRadius: 3, padding: '5px 8px' }}>
      <div style={{ fontSize: 11, color: C.soft }}>{label}</div>
      <div className="font-mono" style={{ fontSize: 13, color: tone ?? C.text, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}
